import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { AdminLayout } from '../../components/admin/AdminLayout';
import { API_BASE } from '../../config/api';

export const PageList = () => {
  const navigate = useNavigate();
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('all');

  const backendUrl = API_BASE;

  useEffect(() => {
    fetchPages();
  }, []);

  const fetchPages = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('admin_token');
      
      if (!token) {
        navigate('/admin/login');
        return;
      }

      const response = await fetch(`${backendUrl}/api/admin/pages`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.status === 401) {
        localStorage.removeItem('admin_token');
        navigate('/admin/login');
        return;
      }

      if (!response.ok) throw new Error('Failed to fetch pages');

      const data = await response.json();
      setPages(data.pages || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this page?')) return;

    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${backendUrl}/api/admin/pages/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) throw new Error('Failed to delete');

      setPages(prev => prev.filter(page => page.id !== id));
    } catch (err) {
      alert('Failed to delete page');
    }
  };

  const togglePublish = async (id, currentStatus) => {
    try {
      const token = localStorage.getItem('admin_token');
      const page = pages.find(p => p.id === id);
      
      const response = await fetch(`${backendUrl}/api/admin/pages/${id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ 
          ...page,
          published: !currentStatus 
        })
      });

      if (!response.ok) throw new Error('Failed to update');

      setPages(prev => 
        prev.map(p => p.id === id ? { ...p, published: !currentStatus } : p)
      );
    } catch (err) {
      alert('Failed to update publish status');
    }
  };

  const filteredPages = pages.filter(page => {
    if (filterStatus === 'all') return true;
    if (filterStatus === 'published') return page.published;
    if (filterStatus === 'draft') return !page.published;
    return true;
  }).sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <div className="inline-block w-12 h-12 border-4 border-[#1E40AF] border-t-transparent rounded-full animate-spin mb-4"></div>
            <p className="text-[#6B7280]">Loading pages...</p>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-[#111827] mb-2" data-testid="pages-title">Pages</h1>
            <p className="text-[#6B7280]">Manage static pages and landing pages</p>
          </div>
          <button
            onClick={() => navigate('/admin/pages/new')}
            className="px-6 py-3 bg-gradient-to-r from-[#1E40AF] to-[#1e3a8a] hover:shadow-xl text-white font-semibold rounded-lg transition-all flex items-center gap-2"
            data-testid="add-page-button"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Add Page
          </button>
        </div>

        {/* Filters */}
        <div className="bg-white border-2 border-[#E5E7EB] rounded-xl p-6">
          <div className="flex items-center gap-4">
            <div className="w-48">
              <label className="block text-sm font-semibold text-[#111827] mb-2">Status</label>
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="w-full px-4 py-2 border-2 border-[#E5E7EB] rounded-lg focus:outline-none focus:border-[#1E40AF]"
                data-testid="filter-status"
              >
                <option value="all">All Pages</option>
                <option value="published">Published</option>
                <option value="draft">Draft</option>
              </select>
            </div>
            <div className="flex-1">
              <p className="text-sm text-[#6B7280] mt-6">
                Showing {filteredPages.length} of {pages.length} pages
              </p>
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="bg-white border-2 border-[#E5E7EB] rounded-xl overflow-hidden">
          {filteredPages.length === 0 ? (
            <div className="text-center py-12">
              <svg className="w-16 h-16 mx-auto text-[#9CA3AF] mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <p className="text-[#6B7280]">No pages found</p>
              <button
                onClick={() => navigate('/admin/pages/new')}
                className="mt-4 text-[#1E40AF] hover:text-[#1e3a8a] font-semibold"
              >
                Create your first page
              </button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="pages-table">
                <thead className="bg-[#F8FAFC]">
                  <tr className="border-b-2 border-[#E5E7EB]">
                    <th className="text-left py-3 px-4 text-sm font-semibold text-[#6B7280]">Title</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-[#6B7280]">Slug</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-[#6B7280]">Status</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-[#6B7280]">Created</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-[#6B7280]">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredPages.map((page) => (
                    <tr
                      key={page.id}
                      className="border-b border-[#E5E7EB] hover:bg-[#F8FAFC] transition-colors"
                      data-testid={`page-row-${page.id}`}
                    >
                      <td className="py-3 px-4 text-sm font-medium text-[#111827]">{page.title}</td>
                      <td className="py-3 px-4 text-sm text-[#6B7280]">/pages/{page.slug}</td>
                      <td className="py-3 px-4">
                        <button
                          onClick={() => togglePublish(page.id, page.published)}
                          className={`px-3 py-1 rounded-full text-xs font-semibold ${
                            page.published
                              ? 'bg-green-100 text-green-800'
                              : 'bg-gray-100 text-gray-800'
                          }`}
                          data-testid={`toggle-publish-${page.id}`}
                        >
                          {page.published ? 'Published' : 'Draft'}
                        </button>
                      </td>
                      <td className="py-3 px-4 text-sm text-[#6B7280]">
                        {new Date(page.created_at).toLocaleDateString()}
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex gap-2">
                          <button
                            onClick={() => navigate(`/admin/pages/${page.id}/edit`)}
                            className="text-[#1E40AF] hover:text-[#1e3a8a] font-semibold text-sm"
                            data-testid={`edit-page-${page.id}`}
                          >
                            Edit
                          </button>
                          <a
                            href={`/#/pages/${page.slug}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-[#6B7280] hover:text-[#111827] font-semibold text-sm"
                          >
                            View
                          </a>
                          <button
                            onClick={() => handleDelete(page.id)}
                            className="text-red-600 hover:text-red-800 font-semibold text-sm"
                            data-testid={`delete-page-${page.id}`}
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
};

export default PageList;
